import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, errMessage } from "@/portal/api";
import { useAuth } from "@/portal/AuthContext";
import { Card, StatusBadge, ServiceTags, STATUS_ORDER, STATUS_META, formatDate } from "@/pages/portal/ui";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { FileText } from "lucide-react";

export default function Applications() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [repId, setRepId] = useState("");
  const [reps, setReps] = useState([]);

  useEffect(() => {
    if (["admin", "manager", "rep"].includes(user.role)) {
      api.get("/api/portal/reps").then((r) => setReps(r.data.reps)).catch(() => {});
    }
  }, [user.role]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (repId) params.rep_id = repId;
      const { data } = await api.get("/api/portal/applications", { params });
      setApps(data.applications);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, [status, repId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-6xl space-y-5" data-testid="applications-page">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setStatus("")}
          className={cn("rounded-full border px-3 py-1 text-xs font-medium transition-colors", status === "" ? "border-navy bg-navy text-white" : "border-line bg-white text-slatey hover:border-navy/40")}
          data-testid="filter-all"
        >
          All
        </button>
        {STATUS_ORDER.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={cn("rounded-full border px-3 py-1 text-xs font-medium transition-colors", status === s ? "border-navy bg-navy text-white" : "border-line bg-white text-slatey hover:border-navy/40")}
            data-testid={`filter-${s}`}
          >
            {STATUS_META[s].label}
          </button>
        ))}
        {["admin", "manager", "rep"].includes(user.role) && reps.length > 0 && (
          <select
            value={repId}
            onChange={(e) => setRepId(e.target.value)}
            data-testid="filter-rep"
            className="ml-auto rounded-md border border-line bg-white px-3 py-1.5 text-xs text-navy"
          >
            <option value="">All reps</option>
            {reps.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        )}
      </div>

      <Card>
        {loading ? (
          <div className="p-10 text-center text-sm text-slatey">Loading…</div>
        ) : apps.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-12 text-center">
            <FileText className="h-8 w-8 text-line" />
            <p className="text-sm text-slatey">No applications found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="applications-table">
              <thead>
                <tr className="border-b border-line text-xs uppercase tracking-wide text-slatey">
                  <th className="px-5 py-3 font-medium">Business</th>
                  <th className="px-5 py-3 font-medium">State</th>
                  <th className="px-5 py-3 font-medium">Services</th>
                  {user.role !== "customer" && <th className="px-5 py-3 font-medium">Rep</th>}
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {apps.map((a) => (
                  <tr key={a.id} className="transition-colors hover:bg-ice" data-testid={`app-row-${a.id}`}>
                    <td className="px-5 py-3">
                      <Link to={`/portal/applications/${a.id}`} className="font-medium text-navy hover:text-electric">
                        {a.company_name}
                      </Link>
                      <p className="text-xs text-slatey">{a.decision_maker?.name}</p>
                    </td>
                    <td className="px-5 py-3 text-slatey">{a.state}</td>
                    <td className="px-5 py-3"><ServiceTags services={a.services} /></td>
                    {user.role !== "customer" && <td className="px-5 py-3 text-slatey">{a.rep_name || "—"}</td>}
                    <td className="px-5 py-3"><StatusBadge status={a.status} /></td>
                    <td className="px-5 py-3 text-slatey">{formatDate(a.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
